<template>
  <BaseModal
    modal-id="account-create-modal"
    title="アカウント新規追加"
    v-model="internalVisible"
    size="lg"
    :center-content="false"
    header-style="gradient"
    no-close-on-backdrop
    no-close-on-esc
    @cancel="$emit('close')"
  >
    <!-- モーダルボディのコンテンツ -->
    <form @submit.prevent="handleSubmit">
      <div class="account-create-form">
        <!-- ユーザーID（入力必須） -->

        <BaseInput
          v-model="formData.userId"
          label="ユーザーID"
          type="text"
          placeholder="ユーザーIDを入力してください"
          :error-message="errors.userId"
          required
          autocomplete="username"
          @update:model-value="handleUserIdInput"
        />

        <!-- 属性（選択必須） -->
        <div class="form-group">
          <label class="form-label">属性 <span class="required">*</span></label>
          <select
            v-model="formData.attribute"
            class="form-control"
            :class="{ 'error': errors.attribute }"
            @change="handleAttributeChange"
          >
            <option value="">属性を選択してください</option>
            <option value="開発者">開発者</option>
            <option value="自治体">自治体</option>
            <option value="清掃業者">清掃業者</option>
          </select>
          <div v-if="errors.attribute" class="error-message">
            {{ errors.attribute }}
          </div>
        </div>

        <!-- 企業（選択必須） -->
        <div class="form-group">
          <label class="form-label">企業 <span class="required">*</span></label>
          <select
            v-model="formData.company"
            class="form-control"
            :class="{ 'error': errors.company }"
            :disabled="!formData.attribute || companyListLoading"
            @change="handleCompanyChange"
          >
            <option value="">
              {{ getCompanyPlaceholder() }}
            </option>
            <option
              v-for="company in companyList"
              :key="company"
              :value="company"
            >
              {{ company }}
            </option>
          </select>
          <div v-if="errors.company" class="error-message">
            {{ errors.company }}
          </div>
        </div>

        <!-- パスワード（入力必須） -->
        <div class="password-input-container">
          <BaseInput
            v-model="formData.password"
            label="パスワード"
            :type="showPassword ? 'text' : 'password'"
            placeholder="英数字を含む6文字以上（例: abc123）"
            :error-message="errors.password"
            description="• 英数字のみ使用可能 • 英語と数字の両方を含む • 6文字以上"
            required
            autocomplete="new-password"
            @update:model-value="handlePasswordInput"
            @enter="handleSubmit"
          />
          <button
            type="button"
            class="password-toggle-btn"
            @click="showPassword = !showPassword"
            :aria-label="showPassword ? 'パスワードを隠す' : 'パスワードを表示'"
          >
            <i :class="showPassword ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
            {{ showPassword ? '隠す' : '表示' }}
          </button>
        </div>
      </div>
    </form>

    <!-- カスタムフッター -->
    <template #footer>
      <div class="modal-actions">
        <BaseButton
          variant="secondary"
          @click="$emit('close')"
        >
          キャンセル
        </BaseButton>
        <BaseButton
          variant="primary"
          @click="handleSubmit"
          :disabled="!isFormValid || internalLoading"
        >
          {{ internalLoading ? '作成中...' : '作成' }}
        </BaseButton>
      </div>
    </template>
  </BaseModal>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import BaseModal from '@/components/molecules/BaseModal.vue'
import BaseButton from '@/components/atoms/BaseButton.vue'
import BaseInput from '@/components/atoms/BaseInput.vue'
import developerSettingsApi from '@/services/developerSettings/developerSettingsService.js'

/**
 * AccountCreateModal - アカウント新規作成モーダルコンポーネント
 *
 * アカウントの新規作成を行うためのモーダルコンポーネントです。
 * BaseModalを基盤として、アカウント作成に必要なフォーム要素を提供します。
 *
 * アトミックデザイン構造:
 * - このコンポーネント: 機能専用モーダル
 *   └── Molecule: BaseModal
 *     └── Bootstrap Vue: b-modal, b-button
 *   └── Atom: BaseInput, BaseButton
 *
 * 責務:
 * - アカウント作成フォームの表示
 * - 入力バリデーション（ユーザーID、属性、企業、パスワード）
 * - パスワード強度チェック（英数字混在、6文字以上）
 * - 作成データの親コンポーネントへの通知
 *
 * 使用例:
 * <AccountCreateModal
 *   :visible="showCreateModal"
 *   @close="showCreateModal = false"
 *   @save="handleAccountCreate"
 * />
 */

/**
 * Props定義
 */
const props = defineProps({
  /**
   * モーダルの表示状態
   */
  visible: {
    type: Boolean,
    default: false
  }
})

/**
 * Emits定義
 */
const emit = defineEmits(['close', 'save'])

/**
 * リアクティブな状態管理
 */
// パスワード表示切替フラグ
const showPassword = ref(false)

const formData = ref({
  userId: '',
  attribute: '',
  company: '',
  password: ''
})

const errors = ref({})
const internalLoading = ref(false)
const companyList = ref([])
const companyListLoading = ref(false)

/**
 * フォームのバリデーション状態
 */
const isFormValid = computed(() => {
  // 全ての必須項目が入力されているかチェック
  if (!formData.value.userId || !formData.value.attribute ||
      !formData.value.company || !formData.value.password) {
    return false
  }
  return true
})

/**
 * BaseModalのv-model用の内部状態
 */
const internalVisible = computed({
  get() {
    return props.visible
  },
  set(value) {
    if (!value) {
      emit('close')
    }
  }
})

/**
 * フォームデータを初期化
 */
const initializeForm = () => {
  formData.value = {
    userId: '',
    attribute: '',
    company: '',
    password: ''
  }
  errors.value = {}
  companyList.value = []
  companyListLoading.value = false
}

/**
 * バリデーションを実行
 */
const validateForm = () => {
  errors.value = {}

  // ユーザーID必須チェック
  if (!formData.value.userId) {
    errors.value.userId = 'ユーザーIDは必須です'
  } else if (formData.value.userId.length < 3) {
    errors.value.userId = 'ユーザーIDは3文字以上で入力してください'
  }

  // 属性必須チェック
  if (!formData.value.attribute) {
    errors.value.attribute = '属性は必須です'
  }

  // 企業必須チェック
  if (!formData.value.company) {
    errors.value.company = '企業は必須です'
  }

  // パスワード必須チェック
  if (!formData.value.password) {
    errors.value.password = 'パスワードは必須です'
  } else if (formData.value.password.length < 6) {
    errors.value.password = 'パスワードは6文字以上で入力してください'
  } else if (!/^[a-zA-Z0-9]+$/.test(formData.value.password)) {
    errors.value.password = 'パスワードは英数字のみで入力してください'
  } else if (!/(?=.*[a-zA-Z])(?=.*[0-9])/.test(formData.value.password)) {
    errors.value.password = 'パスワードは英語と数字の両方を含む必要があります'
  }

  return Object.keys(errors.value).length === 0
}

/**
 * エラーをクリア
 */
const clearError = (field) => {
  if (errors.value[field]) {
    delete errors.value[field]
  }
}

/**
 * ユーザーID入力時の処理
 */
const handleUserIdInput = () => {
  clearError('userId')
}

/**
 * 属性に応じた企業リストを取得
 */
const loadCompanyList = async (attribute) => {
  if (!attribute) {
    companyList.value = []
    return
  }

  companyListLoading.value = true

  try {
    companyList.value = await developerSettingsApi.getCompanyList(attribute)

    // 開発者の場合は自動選択
    if (attribute === '開発者' && companyList.value.length === 1) {
      formData.value.company = companyList.value[0]
    }
  } catch (error) {
    console.error('企業リスト取得エラー:', error)
    companyList.value = []
  } finally {
    companyListLoading.value = false
  }
}

/**
 * 属性変更時の処理
 */
const handleAttributeChange = async () => {
  clearError('attribute')
  formData.value.company = ''
  clearError('company')

  if (formData.value.attribute) {
    await loadCompanyList(formData.value.attribute)
  } else {
    companyList.value = []
  }
}

/**
 * 企業変更時の処理
 */
const handleCompanyChange = () => {
  clearError('company')
}

/**
 * 企業プレースホルダーテキストを取得
 */
const getCompanyPlaceholder = () => {
  if (companyListLoading.value) {
    return '読み込み中...'
  }
  if (!formData.value.attribute) {
    return '属性を先に選択してください'
  }
  if (companyList.value.length === 0) {
    return '企業が見つかりません'
  }
  return '企業を選択してください'
}

/**
 * パスワード入力時の処理
 */
const handlePasswordInput = () => {
  clearError('password')
}

/**
 * フォーム送信処理
 */
const handleSubmit = async () => {
  if (!validateForm()) {
    return
  }

  internalLoading.value = true

  try {
    const createData = {
      userId: formData.value.userId,
      attribute: formData.value.attribute,
      company: formData.value.company,
      password: formData.value.password
    }

    emit('save', createData)
  } catch (error) {
    internalLoading.value = false
  }
}

/**
 * モーダルが表示されたときにフォームを初期化
 */
watch(() => props.visible, (newVisible) => {
  if (newVisible) {
    initializeForm()
  } else {
    internalLoading.value = false
  }
})
</script>

<style lang="scss" scoped>
// アカウント作成フォームのスタイル
.account-create-form {
  padding: 0;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-label {
  display: block;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
}

.required {
  color: #ef4444;
  font-weight: 700;
}

.form-control {
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &.error {
    border-color: #ef4444 !important;
    box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
  }
}

// selectタグ用のスタイル調整
select.form-control {
  background-color: white;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e");
  background-position: right 0.75rem center;
  background-repeat: no-repeat;
  background-size: 1.25rem 1.25rem;
  padding-right: 2.5rem;
  appearance: none;

  &:focus {
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%233b82f6' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e");
  }
}

.error-message {
  color: #ef4444;
  font-size: 0.75rem;
  margin-top: 0.25rem;
  font-weight: 500;
}

.password-help {
  margin-top: 0.5rem;

  small {
    color: #6b7280;
    font-size: 0.75rem;
    line-height: 1.4;
  }
}

.password-input-container {
  position: relative;
}

.password-toggle-btn {
  position: absolute;
  right: 0.75rem;
  top: 2.25rem;
  background: transparent;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 0.5rem;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.375rem;
  transition: color 0.2s;
  z-index: 10;

  &:hover {
    color: #3b82f6;
  }

  &:focus {
    outline: 2px solid #3b82f6;
    outline-offset: 2px;
    border-radius: 0.25rem;
  }

  i {
    font-size: 1rem;
  }
}

.modal-actions {
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
}

// レスポンシブ対応
@media (max-width: 768px) {
  .form-group {
    margin-bottom: 1.25rem;
  }

  .modal-actions {
    flex-direction: column-reverse;
    gap: 0.5rem;

    > * {
      width: 100%;
    }
  }
}
</style>
